import { useEffect, useState } from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link, useNavigate } from "@tanstack/react-router";
import { ChevronDownIcon, UserPlus, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ID } from "appwrite";
import { account } from "@/lib/appwrite";
import { createWorkerProfile } from "@/lib/collections/workerProfile-Users/addWorkerProfile";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "react-toastify";
import { ComboBoxDepartmentSelect, type DepartmentOption } from "@/components/ui/combo-box-department";
import { generateColabCode, getDepartments, getSequenceDocument, incrementSequence, type Department } from "./service/colabCorpCode_generator";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getDepartmentLabel } from "./department/constantes-departamentos/departamentos";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const NewCollaborator = () => {
  const { checkUserStatus } = useAuth();
  const [openBirthday, setOpenBirthday] = useState(false);
  const [dateBirthday, setDateBirthday] = useState<Date | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(true);
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    // Nombre separado en partes
    firstName: "",
    middleName: "",
    lastName: "",
    secondLastName: "",
    // Auth / acceso
    email: "",
    password: "",
    // Datos personales
    birthday: "",
    bloodType: "",
    colabDniCode: "",
    phone1: "",
    phone2: "",
    emergencyContact: "",
    address: "",
    // Datos corporativos
    rolCorp: "",
    occupation: "",
    occupationAbbreviation: "",
    colabCorpCode: "",
    area: "",
    idSupervisor: "",
    departmentId: "",
    // Datos bancarios
    bank: "",
    bankAccountNumber: "",
    bankAccountType: "",
    // Otros (mantenidos por compatibilidad)
    keyFunctions: "",
    archievements: "",
  });

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const depts = await getDepartments();
        const formattedDepts: DepartmentOption[] = depts.map((dept: Department) => ({
          id: dept.$id,
          name: getDepartmentLabel(dept.departmentName),
          number: dept.departmentNumber,
        }));
        setDepartments(formattedDepts);
      } catch (error) {
        console.error("Error cargando departamentos:", error);
      } finally {
        setLoadingDepartments(false);
      }
    };
    loadDepartments();
  }, []);

  // Genera el código automáticamente cuando haya departamento, birthday y secuencia
  useEffect(() => {
    const generateCodePreview = async () => {
      if (form.departmentId && form.birthday && departments.length > 0) {
        try {
          const { currentSequence } = await getSequenceDocument();
          const newSequence = currentSequence + 1;
          const selectedDept = departments.find(d => d.id === form.departmentId);
          if (selectedDept) {
            const generatedCode = generateColabCode(
              selectedDept.number,
              form.birthday,
              newSequence
            );
            setForm((prev) => ({ ...prev, colabCorpCode: generatedCode }));
          }
        } catch (error) {
          console.error("Error generando código preview:", error);
        }
      }
    };
    generateCodePreview();
  }, [form.departmentId, form.birthday, departments]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectBirthday = (selectedDate: Date | undefined) => {
    setDateBirthday(selectedDate);
    if (selectedDate) {
      const isoDate = selectedDate.toISOString();
      setForm((prev) => ({ ...prev, birthday: isoDate }));
    }
    setOpenBirthday(false);
  };

  const handleDepartmentChange = (departmentId: string) => {
    setForm((prev) => ({ ...prev, departmentId }));
  };

  // Concatena los campos de nombre para el auth de Appwrite
  const buildFullName = () => {
    return [form.firstName, form.middleName, form.lastName, form.secondLastName]
      .map(s => s.trim())
      .filter(Boolean)
      .join(" ");
  };

  const validateForm = () => {
    if (!form.firstName.trim()) {
      toast.warning("El primer nombre es obligatorio.");
      return false;
    }
    if (!form.lastName.trim()) {
      toast.warning("El primer apellido es obligatorio.");
      return false;
    }
    if (!form.email.trim()) {
      toast.warning("El correo electrónico es obligatorio.");
      return false;
    }
    if (!emailRegex.test(form.email)) {
      toast.warning("El correo electrónico no es válido.");
      return false;
    }
    if (!form.password.trim()) {
      toast.warning("La contraseña es obligatoria.");
      return false;
    }
    if (form.password.length < 6) {
      toast.warning("La contraseña debe tener al menos 6 caracteres.");
      return false;
    }
    if (!form.birthday.trim()) {
      toast.warning("La fecha de nacimiento es obligatoria.");
      return false;
    }
    if (dateBirthday && dateBirthday > new Date()) {
      toast.warning("La fecha de nacimiento no puede ser posterior al día actual.");
      return false;
    }
    if (!form.departmentId.trim()) {
      toast.warning("Debe seleccionar un departamento.");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (loading) return;
    if (!validateForm()) return;

    setLoading(true);
    const joinDate = new Date().toISOString().split('T')[0];
    const toastId = toast.loading('Guardando colaborador...');

    try {
      // 1. Obtiene la secuencia actual
      const { documentId, currentSequence } = await getSequenceDocument();
      const newSequence = currentSequence + 1;

      // 2. Obtiene el departmentNumber
      const selectedDept = departments.find(d => d.id === form.departmentId);
      if (!selectedDept) {
        throw new Error("Departamento no encontrado");
      }

      // 3. Generar el código de colaborador
      const colabCode = generateColabCode(
        selectedDept.number,
        form.birthday,
        newSequence
      );

      // 4. Concatenar nombre completo para el auth de Appwrite
      const fullName = buildFullName();

      // 5. Crear usuario en Appwrite Auth (name = nombre completo concatenado)
      const newUser = await account.create(
        ID.unique(),
        form.email,
        form.password,
        fullName
      );

      // 6. Crear perfil en la colección con todos los campos nuevos
      await createWorkerProfile({
        userId: newUser.$id,
        // Nombre separado
        firstName: form.firstName,
        middleName: form.middleName || undefined,
        lastName: form.lastName,
        secondLastName: form.secondLastName || undefined,
        // Email también en la colección
        email: form.email,
        // Datos personales
        birthday: form.birthday,
        bloodType: form.bloodType,
        colabDniCode: form.colabDniCode,
        phone1: form.phone1,
        phone2: form.phone2,
        emergencyContact: form.emergencyContact,
        address: form.address,
        // Datos corporativos
        joinDate,
        rolCorp: form.rolCorp,
        occupation: form.occupation,
        occupationAbbreviation: form.occupationAbbreviation,
        colabCorpCode: colabCode,
        area: form.area,
        idSupervisor: form.idSupervisor,
        departmentId: form.departmentId,
        // Datos bancarios
        bank: form.bank,
        bankAccountNumber: form.bankAccountNumber,
        bankAccountType: form.bankAccountType,
        // Otros
        keyFunctions: form.keyFunctions,
        archievements: form.archievements,
      });

      // 7. Actualizar la secuencia
      await incrementSequence(documentId, newSequence);

      // 8. Limpiar formulario
      setForm({
        firstName: '',
        middleName: '',
        lastName: '',
        secondLastName: '',
        email: '',
        password: '',
        birthday: '',
        bloodType: '',
        colabDniCode: '',
        phone1: '',
        phone2: '',
        emergencyContact: '',
        address: '',
        rolCorp: '',
        occupation: '',
        occupationAbbreviation: '',
        colabCorpCode: '',
        area: '',
        idSupervisor: '',
        departmentId: '',
        bank: '',
        bankAccountNumber: '',
        bankAccountType: '',
        keyFunctions: '',
        archievements: '',
      });
      setDateBirthday(undefined);
      checkUserStatus();

      toast.update(toastId, {
        render: `Colaborador creado con éxito. Código: ${colabCode}`,
        type: 'success',
        isLoading: false,
        autoClose: 5000,
      });
    } catch (error: any) {
      console.error('Error creando colaborador:', error);

      let message = 'Error al registrar colaborador. Intenta de nuevo.';
      if (error?.code === 409 || error?.message?.includes('already exists')) {
        message = 'El correo electrónico ya está registrado. Si eliminaste este usuario recientemente, espera unos minutos antes de intentar nuevamente o usa otro correo.';
      } else if (error?.message) {
        message = error.message;
      }

      toast.update(toastId, {
        render: message,
        type: 'error',
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

  return (
    <>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <Users />
            <BreadcrumbLink asChild>
              <BreadcrumbPage>Directorio</BreadcrumbPage>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbLink asChild>
            <Link to="/directory">Colaboradores</Link>
          </BreadcrumbLink>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage className="hidden sm:block">Nuevo colaborador</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="px-4 sm:px-6 lg:px-8 py-6 font-outfit text-center">
        <Card>
          <CardContent className="flex justify-center gap-2 py-4">
            <UserPlus />
            <Label className="text-xl">Agregar nuevo colaborador</Label>
          </CardContent>
        </Card>

        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <div className="py-5 flex flex-col gap-5">

            {/* ── Sección: Datos personales ── */}
            <Card>
              <CardContent className="py-6 text-left">
                <p className={sectionTitleStyle}>Datos personales</p>
                <div className="grid grid-cols-2 gap-4">

                  <div className={divStyle}>
                    <Label htmlFor="firstName" className={labelStyle}>Primer nombre: *</Label>
                    <Input id="firstName" name="firstName" value={form.firstName} onChange={handleChange} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="middleName" className={labelStyle}>Segundo nombre:</Label>
                    <Input id="middleName" name="middleName" value={form.middleName} onChange={handleChange} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="lastName" className={labelStyle}>Primer apellido: *</Label>
                    <Input id="lastName" name="lastName" value={form.lastName} onChange={handleChange} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="secondLastName" className={labelStyle}>Segundo apellido:</Label>
                    <Input id="secondLastName" name="secondLastName" value={form.secondLastName} onChange={handleChange} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="colabDniCode" className={labelStyle}>Número de cédula:</Label>
                    <Input id="colabDniCode" name="colabDniCode" value={form.colabDniCode} onChange={handleChange} maxLength={10} minLength={10} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="bloodType" className={labelStyle}>Tipo de sangre:</Label>
                    <Select value={form.bloodType} onValueChange={(value) => setForm(prev => ({ ...prev, bloodType: value }))}>
                      <SelectTrigger className="w-full border-gray-300 dark:border-gray-600">
                        <SelectValue placeholder="Selecciona el tipo de sangre" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {BLOOD_TYPES.map(type => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="birthday" className={labelStyle}>Fecha de nacimiento: *</Label>
                    <Popover open={openBirthday} onOpenChange={setOpenBirthday}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" id="birthday" className="w-full justify-between font-outfit" type="button">
                          {dateBirthday
                            ? dateBirthday.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
                            : "Seleccionar fecha"}
                          <ChevronDownIcon />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto overflow-hidden p-0" align="start">
                        <Calendar mode="single" selected={dateBirthday} defaultMonth={dateBirthday} captionLayout="dropdown" onSelect={handleSelectBirthday} className="text-gray-400" />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="address" className={labelStyle}>Dirección:</Label>
                    <Input id="address" name="address" value={form.address} onChange={handleChange} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="phone1" className={labelStyle}>Teléfono 1:</Label>
                    <Input id="phone1" name="phone1" value={form.phone1} onChange={handleChange} maxLength={15} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="phone2" className={labelStyle}>Teléfono 2:</Label>
                    <Input id="phone2" name="phone2" value={form.phone2} onChange={handleChange} maxLength={15} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="emergencyContact" className={labelStyle}>Contacto de emergencia:</Label>
                    <Input id="emergencyContact" name="emergencyContact" value={form.emergencyContact} onChange={handleChange} maxLength={15} className={inputStyle} />
                  </div>

                </div>
              </CardContent>
            </Card>

            {/* ── Sección: Datos corporativos ── */}
            <Card>
              <CardContent className="py-6 text-left">
                <p className={sectionTitleStyle}>Datos corporativos</p>
                <div className="grid grid-cols-2 gap-4">

                  <div className={divStyle}>
                    <Label htmlFor="rolCorp" className={labelStyle}>Rol corporativo:</Label>
                    <Input id="rolCorp" name="rolCorp" value={form.rolCorp} onChange={handleChange} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="occupation" className={labelStyle}>Ocupación / Profesión:</Label>
                    <Input id="occupation" name="occupation" value={form.occupation} onChange={handleChange} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="occupationAbbreviation" className={labelStyle}>Abreviatura de ocupación:</Label>
                    <Input id="occupationAbbreviation" name="occupationAbbreviation" value={form.occupationAbbreviation} onChange={handleChange} maxLength={20} className={inputStyle} placeholder="Ej: Ing., Dr., Lic." />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="area" className={labelStyle}>Área:</Label>
                    <Input id="area" name="area" value={form.area} onChange={handleChange} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="departmentId" className={labelStyle}>Departamento: *</Label>
                    {loadingDepartments ? (
                      <Input value="Cargando..." disabled />
                    ) : (
                      <ComboBoxDepartmentSelect
                        departments={departments}
                        value={form.departmentId}
                        placeholder="Seleccionar..."
                        onChange={handleDepartmentChange}
                      />
                    )}
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="colabCorpCode" className={labelStyle}>Código de colaborador:</Label>
                    <Input
                      id="colabCorpCode"
                      name="colabCorpCode"
                      value={form.colabCorpCode}
                      readOnly
                      disabled
                      placeholder="Se generará automáticamente"
                      className="bg-gray-100"
                    />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="idSupervisor" className={labelStyle}>ID Supervisor:</Label>
                    <Input id="idSupervisor" name="idSupervisor" value={form.idSupervisor} onChange={handleChange} className={inputStyle} />
                  </div>

                </div>
              </CardContent>
            </Card>

            {/* ── Sección: Acceso a la plataforma ── */}
            <Card>
              <CardContent className="py-6 text-left">
                <p className={sectionTitleStyle}>Acceso a la plataforma</p>
                <div className="grid grid-cols-2 gap-4">

                  <div className={divStyle}>
                    <Label htmlFor="email" className={labelStyle}>Correo electrónico: *</Label>
                    <Input id="email" name="email" type="email" value={form.email} onChange={handleChange} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="password" className={labelStyle}>Contraseña: *</Label>
                    <Input id="password" name="password" type="password" value={form.password} onChange={handleChange} minLength={8} className={inputStyle} autoComplete="new-password" />
                  </div>

                </div>
              </CardContent>
            </Card>

            {/* ── Sección: Datos bancarios ── */}
            <Card>
              <CardContent className="py-6 text-left">
                <p className={sectionTitleStyle}>Datos bancarios</p>
                <div className="grid grid-cols-2 gap-4">

                  <div className={divStyle}>
                    <Label htmlFor="bank" className={labelStyle}>Banco:</Label>
                    <Input id="bank" name="bank" value={form.bank} onChange={handleChange} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="bankAccountNumber" className={labelStyle}>Número de cuenta:</Label>
                    <Input id="bankAccountNumber" name="bankAccountNumber" value={form.bankAccountNumber} onChange={handleChange} maxLength={18} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="bankAccountType" className={labelStyle}>Tipo de cuenta:</Label>
                    <Input id="bankAccountType" name="bankAccountType" value={form.bankAccountType} onChange={handleChange} className={inputStyle} />
                  </div>

                </div>
              </CardContent>
            </Card>

            {/* ── Botones ── */}
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 px-4 py-2">
              <Button
                type="submit"
                disabled={loading}
                className="bg-blue-500 text-white rounded-2xl w-full sm:w-48 cursor-pointer"
              >
                {loading ? "Guardando..." : "Registrar colaborador"}
              </Button>
              <Button
                type="button"
                className="bg-red-400 text-white rounded-2xl w-full sm:w-48 cursor-pointer"
                onClick={() => navigate({ to: "/directory" })}
              >
                Cancelar
              </Button>
            </div>

          </div>
        </form>
      </div>
    </>
  );
};

const divStyle = "flex flex-col gap-1.5 mx-1";
const labelStyle = "pl-1";
const inputStyle = "border-gray-300 dark:border-gray-600";
const sectionTitleStyle = "text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 pl-1";

export default NewCollaborator;